<template>
  <section
    class="relative w-full overflow-hidden bg-cover bg-center bg-no-repeat after:absolute after:left-0 after:top-0 after:block after:h-full after:w-full after:bg-gradient-to-r after:from-white after:to-white/0 after:content-['']"
    :style="bannerStyle"
  >
    <div
      class="container-wrap relative z-10 flex min-h-mobile-banner py-12 tablet:h-[360px]"
    >
      <div class="flex w-full flex-col items-center justify-center">
        <div class="w-full text-center">
          <p class="text-sm font-bold text-gray-600">
            {{ tGlobal('products.section-name.pricing') }}
          </p>
          <h2 class="text-h2 banner-title mb-2 tablet:mb-4">
            {{ hasPricingPlan ? t('title-1', { productName }) : t('title-2') }}
          </h2>
          <p class="mb-4 text-sm tablet:mb-6 tablet:text-base">
            {{ hasPricingPlan ? t('description-1') : t('description-2') }}
          </p>
          <TheButton
            v-if="hasPricingPlan"
            :to="`/${$i18n.locale}/marketplace/pricing/${$route.params.id}`"
            :title="t('button-1', { productName })"
            class="w-full tablet:w-auto tablet:min-w-[180px]"
          >
            <span class="text-sm">
              {{ t('button-1', { productName }) }}
            </span>
          </TheButton>
          <TheButton
            v-else
            :to="`/${$i18n.locale}/marketplace/products/${$route.params.id}/contact-sales`"
            :title="tGlobal('products.request-a-call')"
            class="w-full tablet:w-[180px]"
          >
            <span class="text-sm">
              {{ tGlobal('products.request-a-call') }}
            </span>
          </TheButton>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
  const { t } = useI18n({ useScope: 'local' });
  const { t: tGlobal } = useI18n({ useScope: 'global' });

  const { blobDomain } = useWebsite();
  const { provideResponsiveImgName } = useImagePath();

  const productsStore = useProductsStore();
  const { productName, hasPricingPlan } = storeToRefs(productsStore);

  const bannerStyle = computed((): string => {
    const name = hasPricingPlan.value
      ? 'pricing-banner_2'
      : 'request-call-banner';
    return `background-image: url(${blobDomain.value}/wise-paas-marketplace/product/${provideResponsiveImgName(name)})`;
  });
</script>

<i18n lang="json">
{
  "en-int": {
    "title-1": "Explore Pricing Plans for {productName}",
    "description-1": "Select from diverse pricing plans designed to fit your specific deployment and infrastructure requirements.",
    "title-2": "Get Your Personalized Quote Today!",
    "description-2": "Our experts are ready to provide a custom pricing plan tailored to your needs. Contact us now!",
    "button-1": "View {productName} Pricing"
  },
  "en-jp": {
    "title-1": "Explore Pricing Plans for {productName}",
    "description-1": "Select from diverse pricing plans designed to fit your specific deployment and infrastructure requirements.",
    "title-2": "Get Your Personalized Quote Today!",
    "description-2": "Our experts are ready to provide a custom pricing plan tailored to your needs. Contact us now!",
    "button-1": "View {productName} Pricing"
  },
  "en-tw": {
    "title-1": "Explore Pricing Plans for {productName}",
    "description-1": "Select from diverse pricing plans designed to fit your specific deployment and infrastructure requirements.",
    "title-2": "Get Your Personalized Quote Today!",
    "description-2": "Our experts are ready to provide a custom pricing plan tailored to your needs. Contact us now!",
    "button-1": "View {productName} Pricing"
  },
  "en-kr": {
    "title-1": "Explore Pricing Plans for {productName}",
    "description-1": "Select from diverse pricing plans designed to fit your specific deployment and infrastructure requirements.",
    "title-2": "Get Your Personalized Quote Today!",
    "description-2": "Our experts are ready to provide a custom pricing plan tailored to your needs. Contact us now!",
    "button-1": "View {productName} Pricing"
  },
  "en-americas": {
    "title-1": "Explore Pricing Plans for {productName}",
    "description-1": "Select from diverse pricing plans designed to fit your specific deployment and infrastructure requirements.",
    "title-2": "Get Your Personalized Quote Today!",
    "description-2": "Our experts are ready to provide a custom pricing plan tailored to your needs. Contact us now!",
    "button-1": "View {productName} Pricing"
  },
  "en-asia-pacific": {
    "title-1": "Explore Pricing Plans for {productName}",
    "description-1": "Select from diverse pricing plans designed to fit your specific deployment and infrastructure requirements.",
    "title-2": "Get Your Personalized Quote Today!",
    "description-2": "Our experts are ready to provide a custom pricing plan tailored to your needs. Contact us now!",
    "button-1": "View {productName} Pricing"
  },
  "en-emea": {
    "title-1": "Explore Pricing Plans for {productName}",
    "description-1": "Select from diverse pricing plans designed to fit your specific deployment and infrastructure requirements.",
    "title-2": "Get Your Personalized Quote Today!",
    "description-2": "Our experts are ready to provide a custom pricing plan tailored to your needs. Contact us now!",
    "button-1": "View {productName} Pricing"
  },
  "ja-jp": {
    "title-1": "{productName} の料金プランを見る",
    "description-1": "特定の導入環境やインフラ要件に合わせて設計された、多様な料金プランからお選びください。",
    "title-2": "今すぐお見積もりを取得！",
    "description-2": "専門家がニーズに合わせた料金プランをご提案します。今すぐお問い合わせください！",
    "button-1": "{productName} の料金を見る"
  },
  "zh-cn": {
    "title-1": "探索 {productName} 的定价方案",
    "description-1": "专为您的基础架构量身定制的弹性价格。",
    "title-2": "立即获取您的专属报价！",
    "description-2": "我们的专家随时为您提供符合您需求的定制定价方案。立即联系我们！",
    "button-1": "查看 {productName} 定价"
  },
  "zh-tw": {
    "title-1": "探索 {productName} 的定價方案",
    "description-1": "多種計價模式供您選擇，專為滿足您特定的部署與基礎架構需求而設計。",
    "title-2": "立即取得您的專屬報價！",
    "description-2": "我們的專家隨時為您提供符合您需求的定制定價方案。立即聯繫我們！",
    "button-1": "查看 {productName} 定價"
  }
}
</i18n>
